import { useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { labelFontStyle, badgeStyle } from '../../styles/typography'

interface TagEditorProps {
  tags: string[]
  onChange: (tags: string[]) => void
  isEditable?: boolean
  placeholder?: string
  className?: string
  showLabel?: boolean
  label?: string
}

export function TagEditor({
  tags = [],
  onChange,
  isEditable = true,
  placeholder = 'Add a tag (e.g., organic, vegan, fair-trade)',
  className = '',
  showLabel = true,
  label = 'Tags'
}: TagEditorProps) {
  const [inputValue, setInputValue] = useState('')

  const handleRemoveTag = (tagToRemove: string) => {
    if (!isEditable) return
    onChange(tags.filter(tag => tag !== tagToRemove))
  }

  const handleAddTag = (newTag: string) => {
    const normalizedTag = newTag.trim().toLowerCase()
    if (normalizedTag && !tags.includes(normalizedTag)) {
      onChange([...tags, normalizedTag])
      setInputValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag(inputValue)
    }
  }

  // View mode - just show tags as badges
  if (!isEditable) {
    if (tags.length === 0) return null

    return (
      <div className={className}>
        {showLabel && (
          <Label style={labelFontStyle}>{label}</Label>
        )}
        <div className="flex flex-wrap gap-2 mt-1">
          {tags.map(tag => (
            <Badge key={tag} variant="outline" style={badgeStyle}>
              {tag}
            </Badge>
          ))}
        </div>
      </div>
    )
  }

  // Edit mode
  return (
    <div className={className}>
      {showLabel && (
        <Label className="flex items-center justify-between" style={labelFontStyle}>
          <span>{label}</span>
          <span className="text-xs text-gray-500 font-normal">
            Click to remove, type to add
          </span>
        </Label>
      )}

      <div className="mt-1">
        {/* Display current tags as removable badges */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map(tag => (
              <Badge
                key={tag}
                variant="outline"
                className="cursor-pointer hover:bg-red-50 hover:border-red-300 transition-colors"
                style={badgeStyle}
                onClick={() => handleRemoveTag(tag)}
              >
                {tag} <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}

        {/* Add new tag input */}
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="text-sm"
            style={badgeStyle}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1" style={badgeStyle}>
          Press Enter to add a tag
        </p>
      </div>
    </div>
  )
}
